import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { localVendorsAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, RefreshCw, Truck, CreditCard, IndianRupee, 
  Plus, ArrowUpRight, ArrowDownLeft, Wallet, Save
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const LocalVendorLedger = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [vendor, setVendor] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_mode: 'cash',
    amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    cheque_number: '',
    cheque_date: '',
    bank_name: '',
    upi_transaction_id: '',
    notes: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);

  const storeType = user?.store_type || 'owned';
  const isFranchise = storeType === 'franchise';
  const canPay = isFranchise || user?.entity_type === 'hq';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [vendorRes, ledgerRes] = await Promise.all([
        localVendorsAPI.get(vendorId),
        localVendorsAPI.getLedger(vendorId, { page_size: 100 })
      ]);
      
      setVendor(vendorRes.data);
      setLedgerEntries(ledgerRes.data.items || []);
      setSummary(ledgerRes.data.summary || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: 'Error', description: 'Failed to fetch vendor details', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [vendorId, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePayment = async () => {
    if (!paymentData.amount || paymentData.amount <= 0) {
      toast({ title: 'Error', description: 'Enter a valid amount', variant: 'destructive' });
      return;
    }

    setSavingPayment(true);
    try {
      await localVendorsAPI.createPayment(vendorId, paymentData);
      toast({ title: 'Success', description: 'Payment recorded successfully' });
      setPaymentDialogOpen(false);
      setPaymentData({
        payment_mode: 'cash',
        amount: 0,
        payment_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        cheque_number: '',
        cheque_date: '',
        bank_name: '',
        upi_transaction_id: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating payment:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to record payment', 
        variant: 'destructive' 
      });
    } finally {
      setSavingPayment(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="p-6 text-center text-gray-500">
        Vendor not found
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="local-vendor-ledger-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/store/local-vendors')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Truck className="w-6 h-6 text-blue-600" />
              {vendor.name}
            </h1>
            <p className="text-gray-500">{vendor.code} • {vendor.phone}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {canPay && (
            <Button 
              onClick={() => setPaymentDialogOpen(true)} 
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Wallet className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Outstanding Balance</p>
                <p className="text-xl font-bold text-red-600">
                  {formatCurrency(summary.current_balance)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ArrowUpRight className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Purchases</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(summary.total_purchases)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Paid</p>
                <p className="text-xl font-bold text-emerald-600">
                  {formatCurrency(summary.total_paid)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ledger Table */}
      <Card>
        <CardHeader className="border-b bg-gray-50/50 py-3">
          <CardTitle className="text-lg">Ledger Entries</CardTitle>
          <CardDescription>Transaction history with this vendor</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ledgerEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No transactions yet
                  </TableCell>
                </TableRow>
              ) : (
                ledgerEntries.map((entry, idx) => (
                  <TableRow key={entry.id || idx}>
                    <TableCell className="text-gray-600">
                      {formatDate(entry.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge className={entry.transaction_type === 'purchase' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-emerald-100 text-emerald-700'}>
                        {entry.transaction_type === 'purchase' ? 'Purchase' : 'Payment'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {entry.reference_number}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {entry.description || '-'}
                    </TableCell>
                    <TableCell className="text-right text-red-600">
                      {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                    </TableCell>
                    <TableCell className="text-right text-emerald-600">
                      {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(entry.balance_after)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Record Payment
            </DialogTitle>
            <DialogDescription>
              Record a payment to {vendor.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-red-700">
                Outstanding Balance: <strong>{formatCurrency(summary.current_balance)}</strong>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Mode *</Label>
                <Select 
                  value={paymentData.payment_mode} 
                  onValueChange={(v) => setPaymentData(p => ({ ...p, payment_mode: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData(p => ({ ...p, payment_date: e.target.value }))}
              />
            </div>

            {paymentData.payment_mode === 'bank_transfer' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    value={paymentData.bank_name}
                    onChange={(e) => setPaymentData(p => ({ ...p, bank_name: e.target.value }))}
                    placeholder="Bank name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reference No.</Label>
                  <Input
                    value={paymentData.reference_number}
                    onChange={(e) => setPaymentData(p => ({ ...p, reference_number: e.target.value }))}
                    placeholder="Transaction reference"
                  />
                </div>
              </div>
            )}

            {paymentData.payment_mode === 'upi' && (
              <div className="space-y-2">
                <Label>UPI Transaction ID</Label>
                <Input
                  value={paymentData.upi_transaction_id}
                  onChange={(e) => setPaymentData(p => ({ ...p, upi_transaction_id: e.target.value }))}
                  placeholder="UPI transaction ID"
                />
              </div>
            )}

            {paymentData.payment_mode === 'cheque' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cheque Number</Label>
                  <Input
                    value={paymentData.cheque_number}
                    onChange={(e) => setPaymentData(p => ({ ...p, cheque_number: e.target.value }))}
                    placeholder="Cheque number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cheque Date</Label>
                  <Input
                    type="date"
                    value={paymentData.cheque_date}
                    onChange={(e) => setPaymentData(p => ({ ...p, cheque_date: e.target.value }))}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={paymentData.notes}
                onChange={(e) => setPaymentData(p => ({ ...p, notes: e.target.value }))}
                placeholder="Payment notes..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePayment} 
              disabled={savingPayment}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingPayment ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Record Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocalVendorLedger;
